/* eslint-disable max-statements */
import {useCallback, useRef, useState} from 'react';

import {
    HeadlessManagerType,
    HeadlessVaultManager,
    ICardPaymentMethodManager,
    PaymentMethodInfo,
    PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';

import {useDispatch} from 'react-redux';

import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {useConfigureAndStartPrimer} from 'desktop/pages/checkout/hooks/primer/useConfigureAndStartPrimer';
import {useGetErrorAndSendAnalytics} from 'desktop/pages/checkout/hooks/primer/useGetErrorAndSendAnalytics/useGetErrorAndSendAnalytics';
import {setOptionPaymentMethod} from 'desktop/pages/checkout/store/reducers/analytics/analytics';
import {setIsCreditCardLoading} from 'desktop/pages/checkout/store/reducers/primer/primer';

import {PrimerErrorType, defaultErrorState} from '../constants';

import {sendPayerInfoToSaga} from './../../../store/reducers/paymentStep/payerInfo/payerInfo';

import {useConfigureCard} from './useConfigureCard';
import {useSubmitPrimerCreditCard} from './useSubmitPrimerCreditCard';

type PropsType = {
    onReady: () => void;
};

/**
 * hook for Primer card payment
 */
export const usePrimerCreditCard = ({onReady}: PropsType) => {
    const primerRef = useRef<PrimerHeadlessCheckout | null>(null);
    const managerRef = useRef<ICardPaymentMethodManager | null>(null);
    const vaultManagerRef = useRef<HeadlessVaultManager | null>(null);
    const [primerError, setPrimerError] = useState<PrimerErrorType>(defaultErrorState);
    const dispatch = useDispatch();
    const {assetsRef, cardNetwork, configureCard} = useConfigureCard({
        managerRef,
        primerRef,
        setPrimerError,
    });

    const onAvailablePaymentMethodsLoad = useCallback(
        (paymentMethods: PaymentMethodInfo[]) => {
            if (paymentMethods.find(method => method.managerType === HeadlessManagerType.CARD))
                configureCard();
        },
        [configureCard],
    );

    const {error} = useGetErrorAndSendAnalytics(PAYMENTS.METHOD_PRIMER);

    useConfigureAndStartPrimer({
        onAvailablePaymentMethodsLoad,
        onCheckoutFail: () => {
            dispatch(setIsCreditCardLoading(false));
            window.onbeforeunload = null;
        },
        onPrimerHeadlessReady: primerHeadless => {
            primerRef.current = primerHeadless;
            assetsRef.current = primerHeadless.getAssetsManager();
        },
        onReady,
        onVaultMangerReady: vaultManager => {
            vaultManagerRef.current = vaultManager;
        },
        runSavePayment: ({orderId, token}) =>
            dispatch(
                sendPayerInfoToSaga({
                    method: PAYMENTS.METHOD_PRIMER,
                    orderIncrementId: orderId,
                    token,
                }),
            ),
        setOptionPayment: () => dispatch(setOptionPaymentMethod()),
    });

    const submitForm = useSubmitPrimerCreditCard({
        cardNetwork,
        managerRef,
        setPrimerError,
        vaultManagerRef,
    });

    return {
        cardNetwork,
        primerError:
            error ||
            primerError['cardNumber-card'] ||
            primerError['expiryDate-card'] ||
            primerError['cvv-card'],
        setPrimerError,
        submitForm,
    };
};
